// The board shell: selection, tools, keyboard, paste/drop and every action that
// turns an intent into ops. Engine-agnostic; the canvas engine only draws and
// reports gestures (see engine/types.ts).

import { type Anchor, applyOps, groupMembers, type Item, type Op } from '@shared/board'
import { newId } from '@shared/ids'
import { normalise, type Rect, round, union, type Vec } from '@shared/whiteboard/geometry'
import {
  adoptedBy,
  canResizeGroup,
  fitFrames,
  membershipOnDrop,
  withMembership,
} from '@shared/whiteboard/membership'
import {
  anchorSpot,
  hiddenReplies,
  indexThreads,
  replySpot,
  threadRootOf,
} from '@shared/whiteboard/threads'
import { blocksToCardMarkdown, writeCard } from '@shared/card-markdown'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { flushSync } from 'react-dom'
import { toast } from 'sonner'
import * as Y from 'yjs'
import { useI18n } from '@/i18n/i18n'
import { isComposing } from '@/lib/ime'
import { TitleEditingContext, useBoardCtx } from './context'
import { XyflowEngine } from './engine/xyflow/XyflowEngine'
import { type BoardLink, type EngineApi, type MoveEnd } from './engine/types'
import {
  boundsOf,
  CARD_W,
  MESSAGE_W,
  newCard,
  newFileItem,
  newMaterialItem,
  newGroup,
  newLink,
  newMessage,
  messageToCardMarkdown,
  spotNear,
} from './factory'
import { ChangeOverlay } from './ChangeOverlay'
import { BoardDerivedContext, type BoardDerived, FoldStore } from './derived'
import { MessageComposer } from './MessageComposer'
import { Toolbar } from './Toolbar'
import { imageSize, isProbablyUrl, uploadFile } from './upload'
import { addMaterial, MaterialError } from '@/materials/prepare'
import {
  activeBlockDrag,
  type BlockDrag,
  endBlockDrag,
  removeFromSource,
  restoreToSource,
} from './cardEditor/blockDrag'

const isPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)

type Tool = 'select' | 'group'

function isEditable(target: EventTarget | null) {
  const el = target as HTMLElement | null
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
}

const NO_SELECTION: ReadonlySet<string> = new Set()

/** As many things as a message can point at (shared/board.ts anchorSchema). */
const MAX_ANCHOR_ITEMS = 100

/** Where the message composer is open, and what the new message will point at or reply to. */
type Composer = { at: Vec; anchor?: Anchor; replyTo?: string }

export function BoardCanvas() {
  const {
    client,
    boardId,
    me,
    autofocus,
    initialText,
    engine: shared,
    actions,
    sessions,
  } = useBoardCtx()
  const { t } = useI18n()
  const snap = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const engine = useRef<EngineApi | null>(null)
  const [selected, setSelected] = useState<ReadonlySet<string>>(NO_SELECTION)
  const [selectedEdges, setSelectedEdges] = useState<ReadonlySet<string>>(NO_SELECTION)
  const [tool, setTool] = useState<Tool>('select')
  const [composer, setComposer] = useState<Composer | null>(null)
  /**
   * Open the composer now, inside the event that asked for it: it takes focus
   * as it mounts, so keys typed right after a double-click land in it rather
   * than on the board. (React Flow's double-click is a native listener, whose
   * updates React would otherwise render a moment later.)
   */
  const openComposer = useCallback((c: Composer) => {
    flushSync(() => {
      setComposer(c)
    })
  }, [])
  const [folds] = useState(() => new FoldStore(boardId))
  const folded = useSyncExternalStore(folds.subscribe, folds.getSnapshot)
  const derived = useMemo((): BoardDerived => {
    const index = indexThreads(snap.state)
    return { ...index, folded, hidden: hiddenReplies(index, folded) }
  }, [snap.state, folded])
  // Read inside keyboard handlers without re-subscribing on every change.
  const hiddenNow = useRef(derived.hidden)
  useLayoutEffect(() => {
    hiddenNow.current = derived.hidden
  }, [derived.hidden])
  const [editingTitle, setEditingTitle] = useState<string | null>(null)
  const titleEditing = useMemo(() => ({ id: editingTitle, set: setEditingTitle }), [editingTitle])

  useEffect(() => client.onReject(() => toast(t('board.conflict'))), [client, t])

  const center = useCallback((): Vec => engine.current?.viewportCenter() ?? { x: 0, y: 0 }, [])
  const select = useCallback((ids: Iterable<string>) => {
    setSelected(new Set(ids))
    setSelectedEdges(NO_SELECTION)
  }, [])

  // ---------- creation ----------

  const createCard = useCallback(
    (at?: Vec, text?: string) => {
      const p = spotNear(client.view, at ?? center(), CARD_W, 160)
      const card = newCard(client.view, me, p.x, p.y)
      autofocus.add(card.id)
      if (text !== undefined) initialText.set(card.id, text)
      client.apply(withMembership(client.view, [{ type: 'item.create', item: card }]))
      select([card.id])
    },
    [client, me, autofocus, initialText, center, select],
  )

  // ---------- messages: about something, replies, threads ----------

  /** The board with items at the size they are drawn now: placement must not use a stale height. */
  const asDrawn = useCallback(() => {
    const s = client.view
    const items = new Map(s.items)
    for (const [id, it] of items) {
      const d = engine.current?.drawnSize(id)
      if (d && (Math.abs(d.h - it.h) > 1 || Math.abs(d.w - it.w) > 1))
        items.set(id, { ...it, w: d.w, h: d.h })
    }
    return { ...s, items }
  }, [client])

  const sayAbout = useCallback(
    (anchor: Anchor, nearY?: number) => {
      const at = anchorSpot(
        asDrawn(),
        anchor,
        MESSAGE_W,
        90,
        nearY === undefined ? undefined : { y: nearY },
      )
      openComposer({ at, anchor })
    },
    [asDrawn, openComposer],
  )

  const reply = useCallback(
    (messageId: string) => {
      // Replying into a folded thread opens it: what is being written shows.
      folds.setFolded(threadRootOf(client.view, messageId), false)
      openComposer({ at: replySpot(asDrawn(), messageId, MESSAGE_W, 90), replyTo: messageId })
    },
    [client, folds, asDrawn, openComposer],
  )

  const toggleThread = useCallback(
    (rootId: string) => {
      folds.setFolded(rootId, !folds.getSnapshot().has(rootId))
    },
    [folds],
  )

  const cardFromMessage = useCallback(
    (messageId: string) => {
      const m = client.view.items.get(messageId)
      if (m?.kind !== 'message') return
      const p = spotNear(client.view, { x: m.x + m.w + 60 + CARD_W / 2, y: m.y + 80 }, CARD_W, 160)
      const card = newCard(client.view, me, p.x, p.y)
      const now = Date.now()
      client.apply([
        { type: 'item.create', item: card },
        {
          type: 'edge.create',
          edge: {
            id: newId(),
            from: card.id,
            to: m.id,
            label: '',
            createdBy: me,
            createdAt: now,
            updatedAt: now,
            version: 0,
          },
        },
      ])
      // Through the card's markdown, like any text written into a card: a short
      // title, the words as the body, and links (mentions) still links.
      writeCard(client.doc, card.id, messageToCardMarkdown(m.data.text), null)
      select([card.id])
    },
    [client, me, select],
  )

  /** Blocks dragged out of a card: a new card where they were dropped, the old one without them. */
  const cardFromBlocks = useCallback(
    (block: BlockDrag, at: Vec) => {
      endBlockDrag(block.editor)
      // The drop point is where the card's top-left grip would be.
      const card = newCard(client.view, me, Math.round(at.x - 20), Math.round(at.y - 14))
      client.apply(withMembership(client.view, [{ type: 'item.create', item: card }]))
      writeCard(client.doc, card.id, blocksToCardMarkdown(block.blocks), null)
      removeFromSource(block)
      select([card.id])
      toast(t('block.movedOut'), {
        action: {
          label: t('block.undoMove'),
          onClick: () => {
            if (client.view.items.has(card.id)) client.apply([{ type: 'item.delete', id: card.id }])
            restoreToSource(block)
          },
        },
      })
    },
    [client, me, select, t],
  )

  const showItems = useCallback(
    (ids: string[]) => {
      const rect = union(ids.map((id) => client.view.items.get(id)).filter((i) => !!i))
      if (!rect || !engine.current) return
      engine.current.fitRect(rect)
      engine.current.flash(ids)
    },
    [client],
  )

  useEffect(() => {
    actions.set({ sayAbout, reply, toggleThread, cardFromMessage, showItems })
    return () => {
      actions.set(null)
    }
  }, [actions, sayAbout, reply, toggleThread, cardFromMessage, showItems])

  /**
   * M: say something about the selection. A very large selection inside one
   * group points at the group; otherwise it is capped (quietly said so).
   */
  const sayAboutSelection = useCallback(() => {
    const ids = [...selected].filter((id) => client.view.items.has(id))
    if (!ids.length) return
    if (ids.length <= MAX_ANCHOR_ITEMS) {
      sayAbout({ items: ids })
      return
    }
    const groups = new Set(ids.map((id) => client.view.items.get(id)?.groupId ?? null))
    const only = groups.size === 1 ? [...groups][0] : null
    if (only) {
      sayAbout({ items: [only] })
      return
    }
    toast(t('anchor.capped', { n: MAX_ANCHOR_ITEMS }))
    sayAbout({ items: ids.slice(0, MAX_ANCHOR_ITEMS) })
  }, [client, selected, sayAbout, t])

  /** A new group around `rect`, adopting `members`. One undo step. */
  const createGroup = useCallback(
    (rect: Rect, members: Item[]) => {
      const group = newGroup(client.view, me, round(rect), '')
      setEditingTitle(group.id)
      client.apply([
        { type: 'item.create', item: group },
        ...members.map((m): Op => ({
          type: 'item.update',
          id: m.id,
          patch: { groupId: group.id },
        })),
      ])
      select([group.id])
    },
    [client, me, select],
  )

  /** G: wrap the selection if there is one, otherwise start drawing a group. */
  const groupCommand = useCallback(() => {
    const picked = [...selected]
      .map((id) => client.view.items.get(id))
      .filter((i): i is Item => !!i && i.kind !== 'group')
    if (picked.length) createGroup(boundsOf(picked), picked)
    else setTool((cur) => (cur === 'group' ? 'select' : 'group'))
  }, [client, selected, createGroup])

  /** A PDF becomes a material (MATERIALS.md): prepared here, then placed as a handle. */
  const addPdf = useCallback(
    async (file: File, at: Vec) => {
      const id = toast.loading(t('upload.progress', { name: file.name }))
      try {
        const material = await addMaterial(boardId, file, (pr) => {
          toast.loading(
            t(pr.phase === 'reading' ? 'material.reading' : 'material.uploading', {
              name: file.name,
              done: pr.done,
              total: pr.total,
            }),
            { id },
          )
        })
        client.addKnownMaterial(material)
        client.apply(
          withMembership(client.view, [
            { type: 'item.create', item: newMaterialItem(client.view, me, at, material) },
          ]),
        )
        toast.dismiss(id)
      } catch (e) {
        const msg =
          e instanceof Error && e.message === 'too_large'
            ? t('upload.tooLarge')
            : e instanceof MaterialError && e.message === 'too_many_pages'
              ? t('material.tooManyPages', { name: file.name })
              : t('material.failed', { name: file.name })
        toast.error(msg, { id })
      }
    },
    [boardId, client, me, t],
  )

  const addFiles = useCallback(
    async (files: File[], at?: Vec) => {
      const p = at ?? center()
      for (const file of files) {
        if (isPdf(file)) {
          await addPdf(file, p)
          continue
        }
        const id = toast.loading(t('upload.progress', { name: file.name }))
        try {
          const [uploaded, natural] = await Promise.all([
            uploadFile(boardId, file),
            imageSize(file),
          ])
          client.apply(
            withMembership(client.view, [
              {
                type: 'item.create',
                item: newFileItem(client.view, me, p.x, p.y, uploaded, natural),
              },
            ]),
          )
          toast.dismiss(id)
        } catch (e) {
          toast.error(
            e instanceof Error && e.message === 'too_large'
              ? t('upload.tooLarge')
              : t('upload.failed'),
            {
              id,
            },
          )
        }
      }
    },
    [boardId, client, me, t, center, addPdf],
  )

  const addLink = useCallback(
    (url: string, at?: Vec) => {
      const c = at ?? center()
      const link = newLink(client.view, me, c.x, c.y, url)
      client.apply(withMembership(client.view, [{ type: 'item.create', item: link }]))
      void fetch('/api/link-preview', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url }),
      })
        .then((r) => (r.ok ? (r.json() as Promise<Record<string, unknown>>) : null))
        .then((meta) => {
          if (!meta || !client.view.items.has(link.id)) return
          const rest = { ...meta }
          delete rest.url
          // Metadata is not something the person did, so it is not an undo step.
          client.apply([{ type: 'item.update', id: link.id, patch: { data: rest } }], {
            undoable: false,
          })
        })
    },
    [client, me, center],
  )

  // ---------- copies ----------

  /**
   * Copy items (a group brings its members) to `place(item)`. Card text is
   * copied in the Y.Doc; edges between copied items are copied too.
   */
  const duplicate = useCallback(
    (picked: Iterable<string>, place: (item: Item) => Vec) => {
      const ids = [...picked]
      const s = client.view
      const set = new Set<string>()
      for (const id of ids) {
        set.add(id)
        if (s.items.get(id)?.kind === 'group') for (const m of groupMembers(s, id)) set.add(m.id)
      }
      const fresh = new Map([...set].map((id) => [id, newId()]))
      const now = Date.now()
      const ops: Op[] = []
      const created: Item[] = []
      // Groups first, so members can reference them.
      const order = [...set].sort(
        (a, b) =>
          Number(s.items.get(b)?.kind === 'group') - Number(s.items.get(a)?.kind === 'group'),
      )
      for (const id of order) {
        const it = s.items.get(id)
        const nid = fresh.get(id)
        if (!it || !nid) continue
        const p = place(it)
        const groupId = it.groupId ? (fresh.get(it.groupId) ?? it.groupId) : null
        const copy = {
          ...it,
          id: nid,
          x: p.x,
          y: p.y,
          groupId,
          createdBy: me,
          createdAt: now,
          updatedAt: now,
          version: 0,
        }
        created.push(copy)
        ops.push({ type: 'item.create', item: copy })
        if (it.kind === 'card') {
          const from = client.doc.getXmlFragment(`card:${id}`)
          const to = client.doc.getXmlFragment(`card:${nid}`)
          const nodes = from
            .toArray()
            .filter(
              (n): n is Y.XmlElement | Y.XmlText =>
                n instanceof Y.XmlElement || n instanceof Y.XmlText,
            )
            .map((n) => n.clone())
          client.doc.transact(() => {
            to.insert(0, nodes)
          })
        }
      }
      for (const e of s.edges.values()) {
        const from = fresh.get(e.from)
        const to = fresh.get(e.to)
        if (from && to)
          ops.push({
            type: 'edge.create',
            edge: {
              ...e,
              id: newId(),
              from,
              to,
              createdBy: me,
              createdAt: now,
              updatedAt: now,
              version: 0,
            },
          })
      }
      try {
        const after = applyOps(s, ops, now)
        // Copies whose group was copied with them keep it; the rest follow the drop rules.
        const copiedGroups = new Set(created.filter((c) => c.kind === 'group').map((c) => c.id))
        const loose = created
          .filter((c) => c.kind !== 'group' && !(c.groupId && copiedGroups.has(c.groupId)))
          .map((c) => c.id)
        client.apply([...ops, ...membershipOnDrop(after, loose)])
      } catch {
        client.apply(ops)
      }
      select(ids.map((id) => fresh.get(id)).filter((id): id is string => !!id))
    },
    [client, me, select],
  )

  // ---------- engine intents ----------

  const onMoveEnd = useCallback(
    ({ positions, grabbed, duplicate: copy }: MoveEnd) => {
      if (copy) {
        duplicate(grabbed, (it) => positions.get(it.id) ?? { x: it.x + 24, y: it.y + 24 })
        return
      }
      const ops: Op[] = [...positions].map(([id, p]) => ({
        type: 'item.update',
        id,
        patch: { x: p.x, y: p.y },
      }))
      const followers = new Set([...positions.keys()].filter((id) => !grabbed.includes(id)))
      try {
        const after = applyOps(client.view, ops, Date.now())
        const movedOnTheirOwn = grabbed.filter((id) => !followers.has(id))
        client.apply([...ops, ...membershipOnDrop(after, movedOnTheirOwn)])
      } catch {
        client.apply(ops)
      }
    },
    [client, duplicate],
  )

  const onResizeEnd = useCallback(
    (id: string, rect: Rect) => {
      const it = client.view.items.get(id)
      if (!it) return
      if (it.kind === 'card') {
        const curH = it.data.fixedHeight ?? it.h
        const patch: Extract<Op, { type: 'item.update' }>['patch'] = {
          x: rect.x,
          y: rect.y,
          w: rect.w,
        }
        if (Math.abs(rect.h - curH) > 1) patch.data = { fixedHeight: rect.h }
        client.apply([{ type: 'item.update', id, patch }])
      } else if (it.kind === 'group' && !canResizeGroup(client.view, id, rect)) {
        return
      } else {
        client.apply([
          { type: 'item.update', id, patch: { x: rect.x, y: rect.y, w: rect.w, h: rect.h } },
        ])
      }
    },
    [client],
  )

  const canResize = useCallback(
    (id: string, rect: Rect) =>
      client.view.items.get(id)?.kind !== 'group' || canResizeGroup(client.view, id, rect),
    [client],
  )

  const onResetHeight = useCallback(
    (id: string) => {
      const it = client.view.items.get(id)
      if (it?.kind === 'card' && it.data.fixedHeight != null) {
        client.apply([{ type: 'item.update', id, patch: { data: { fixedHeight: null } } }])
      }
    },
    [client],
  )

  const onConnect = useCallback(
    (from: string, to: string) => {
      if (from === to) return
      const dup = [...client.view.edges.values()].some(
        (e) => (e.from === from && e.to === to) || (e.from === to && e.to === from),
      )
      if (dup) return
      const now = Date.now()
      client.apply([
        {
          type: 'edge.create',
          edge: {
            id: newId(),
            from,
            to,
            label: '',
            createdBy: me,
            createdAt: now,
            updatedAt: now,
            version: 0,
          },
        },
      ])
    },
    [client, me],
  )

  const onDelete = useCallback(
    (items: string[], edges: string[]) => {
      const gone = new Set(items)
      const ops: Op[] = [
        ...edges
          .filter((id) => {
            const e = client.view.edges.get(id)
            return e && !gone.has(e.from) && !gone.has(e.to)
          })
          .map((id): Op => ({ type: 'edge.delete', id })),
        ...items.map((id): Op => ({ type: 'item.delete', id })),
      ]
      // Groups that lost members fit their frames to what is left (when frames shrink).
      const groups = items.flatMap((id) => {
        const g = client.view.items.get(id)?.groupId
        return g && !gone.has(g) ? [g] : []
      })
      if (ops.length && groups.length) {
        try {
          ops.push(...fitFrames(applyOps(client.view, ops, Date.now()), groups))
        } catch {
          // the deletes alone
        }
      }
      if (ops.length) client.apply(ops)
      select([])
    },
    [client, select],
  )

  const onSelectionChange = useCallback((items: Set<string>, edges: Set<string>) => {
    setSelected(items)
    setSelectedEdges(edges)
  }, [])
  const onMeasure = useCallback(
    (id: string, w: number, h: number) => {
      client.measure(id, w, h)
    },
    [client],
  )
  const onReady = useCallback(
    (api: EngineApi) => {
      engine.current = api
      shared.set(api)
    },
    [shared],
  )
  // A side canvas can close: the chat must not move a camera that is gone.
  useEffect(
    () => () => {
      shared.set(null)
    },
    [shared],
  )

  // ---------- keyboard, paste ----------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (client.readOnly || isComposing(e) || isEditable(e.target)) return
      const mod = e.metaKey || e.ctrlKey
      const key = e.key.toLowerCase()
      if (mod && key === 'z') {
        e.preventDefault()
        if (e.shiftKey) client.redo()
        else client.undo()
      } else if (mod && key === 'y') {
        e.preventDefault()
        client.redo()
      } else if (mod && key === 'a') {
        e.preventDefault()
        // Everything drawn: replies in folded threads stay out of it.
        select([...client.view.items.keys()].filter((id) => !hiddenNow.current.has(id)))
      } else if (mod && key === 'd') {
        e.preventDefault()
        if (selected.size) duplicate(selected, (it) => ({ x: it.x + 24, y: it.y + 24 }))
      } else if (!mod && !e.altKey && key === 'c') {
        e.preventDefault()
        createCard()
      } else if (!mod && !e.altKey && key === 'g') {
        e.preventDefault()
        groupCommand()
      } else if (!mod && !e.altKey && key === 'm' && selected.size) {
        e.preventDefault()
        sayAboutSelection()
      } else if (e.key.startsWith('Arrow') && selected.size) {
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        const d = {
          ArrowLeft: [-step, 0],
          ArrowRight: [step, 0],
          ArrowUp: [0, -step],
          ArrowDown: [0, step],
        }[e.key]
        if (!d) return
        const positions = new Map<string, Vec>()
        const grabbed = [...selected]
        for (const id of grabbed) {
          const it = client.view.items.get(id)
          if (!it) continue
          positions.set(id, { x: it.x + (d[0] ?? 0), y: it.y + (d[1] ?? 0) })
          if (it.kind === 'group') {
            for (const m of groupMembers(client.view, id)) {
              if (!positions.has(m.id))
                positions.set(m.id, { x: m.x + (d[0] ?? 0), y: m.y + (d[1] ?? 0) })
            }
          }
        }
        onMoveEnd({ positions, grabbed, duplicate: false })
      } else if (e.key === 'Escape') {
        if (tool !== 'select') setTool('select')
        else select([])
      }
    }
    const onPaste = (e: ClipboardEvent) => {
      if (isEditable(e.target)) return
      const files = [...(e.clipboardData?.files ?? [])]
      if (files.length) {
        e.preventDefault()
        void addFiles(files)
        return
      }
      const text = e.clipboardData?.getData('text/plain').trim()
      if (!text) return
      e.preventDefault()
      if (isProbablyUrl(text)) addLink(text)
      else createCard(undefined, text)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('paste', onPaste)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('paste', onPaste)
    }
  }, [
    client,
    selected,
    tool,
    createCard,
    groupCommand,
    addFiles,
    addLink,
    duplicate,
    onMoveEnd,
    select,
    sayAboutSelection,
  ])

  const items = snap.state.items
  const hidden = derived.hidden
  const links = useMemo((): BoardLink[] => {
    const out: BoardLink[] = []
    for (const m of snap.state.items.values()) {
      if (m.kind !== 'message' || hidden.has(m.id)) continue
      for (const target of m.data.anchor?.items ?? [])
        if (snap.state.items.has(target) && !hidden.has(target))
          out.push({ id: `a:${m.id}:${target}`, from: m.id, to: target, kind: 'anchor' })
      const parent = m.data.replyTo
      if (parent && snap.state.items.get(parent)?.kind === 'message')
        out.push({ id: `r:${m.id}`, from: m.id, to: parent, kind: 'reply' })
    }
    return out
  }, [snap.state, hidden])

  /**
   * Clicking a conversation on the board opens it. A plain click on a message
   * only selects it (people arrange things by clicking); its author line and
   * its "show in the conversation" action switch the chat.
   */
  const onItemClick = useCallback(
    (id: string) => {
      const it = client.view.items.get(id)
      if (it?.kind === 'session') sessions.reveal(it.data.sessionId, null, true)
    },
    [client, sessions],
  )

  return (
    <div
      className="relative h-full"
      onDragOver={(e) => {
        if (activeBlockDrag()) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
        } else if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
        }
      }}
      onDrop={(e) => {
        const block = activeBlockDrag()
        if (block) {
          // A drop into a card's text is that card's to handle.
          if (e.target instanceof Element && e.target.closest('.ProseMirror')) return
          e.preventDefault()
          if (engine.current)
            cardFromBlocks(block, engine.current.screenToBoard({ x: e.clientX, y: e.clientY }))
          return
        }
        const files = [...e.dataTransfer.files]
        if (!files.length || !engine.current) return
        e.preventDefault()
        void addFiles(files, engine.current.screenToBoard({ x: e.clientX, y: e.clientY }))
      }}
    >
      <BoardDerivedContext value={derived}>
        <TitleEditingContext value={titleEditing}>
          <XyflowEngine
            state={snap.state}
            ready={snap.ready}
            selectedItems={selected}
            selectedEdges={selectedEdges}
            onSelectionChange={onSelectionChange}
            onMoveEnd={onMoveEnd}
            onResizeEnd={onResizeEnd}
            canResize={canResize}
            onResetHeight={onResetHeight}
            onConnect={onConnect}
            onDelete={onDelete}
            onMeasure={onMeasure}
            onPaneDoubleClick={(at) => {
              if (!client.readOnly) openComposer({ at })
            }}
            onItemClick={onItemClick}
            hidden={hidden}
            links={links}
            onReady={onReady}
            interactive={tool === 'select'}
            editable={!client.readOnly}
            viewportKey={boardId}
            worldOverlay={
              <>
                <ChangeOverlay />
                {composer && (
                  <div
                    className="absolute"
                    style={{ transform: `translate(${composer.at.x}px, ${composer.at.y}px)` }}
                  >
                    <MessageComposer
                      width={MESSAGE_W}
                      placeholder={
                        composer.replyTo
                          ? t('composer.reply')
                          : composer.anchor
                            ? t('composer.about')
                            : t('composer.placeholder')
                      }
                      onCancel={() => {
                        setComposer(null)
                      }}
                      onSubmit={(text, handToAi) => {
                        const message = newMessage(
                          client.view,
                          me,
                          composer.at.x,
                          composer.at.y,
                          text,
                          {
                            ...(composer.anchor && { anchor: composer.anchor }),
                            ...(composer.replyTo && { replyTo: composer.replyTo }),
                          },
                        )
                        client.apply(
                          withMembership(client.view, [{ type: 'item.create', item: message }]),
                        )
                        if (handToAi) sessions.fire(null, [message.id])
                        setComposer(null)
                      }}
                    />
                  </div>
                )}
              </>
            }
          />
        </TitleEditingContext>
      </BoardDerivedContext>

      {tool === 'group' && (
        <GroupDrawLayer
          toBoard={(p) => engine.current?.screenToBoard(p) ?? p}
          onDone={(rect) => {
            setTool('select')
            if (rect) createGroup(rect, adoptedBy(client.view, rect))
          }}
        />
      )}

      {snap.ready && items.size === 0 && !composer && tool === 'select' && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <p className="max-w-xs text-center text-[13.5px] leading-relaxed text-balance text-muted">
            {t('board.emptyHint')}
          </p>
        </div>
      )}

      {!client.readOnly && (
        <Toolbar
          canUndo={snap.canUndo}
          canRedo={snap.canRedo}
          hasSelection={selected.size > 0}
          groupActive={tool === 'group'}
          onSayAbout={sayAboutSelection}
          onCard={() => {
            createCard()
          }}
          onGroup={groupCommand}
          onFiles={(files) => void addFiles(files)}
          onUndo={() => {
            client.undo()
          }}
          onRedo={() => {
            client.redo()
          }}
        />
      )}
    </div>
  )
}

/**
 * Drawing a group: a layer over the canvas that captures one drag. Too small a
 * drag (a click) makes a default-sized group at that spot. Esc cancels (handled
 * by the shell, which unmounts this layer).
 */
function GroupDrawLayer({
  toBoard,
  onDone,
}: {
  toBoard: (p: Vec) => Vec
  onDone: (rect: Rect | null) => void
}) {
  const { t } = useI18n()
  const [drag, setDrag] = useState<{ a: Vec; b: Vec } | null>(null)
  const layer = useRef<HTMLDivElement>(null)
  const local = (e: React.PointerEvent) => {
    const r = layer.current?.getBoundingClientRect()
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) }
  }
  const box = drag && normalise(drag.a, drag.b)
  return (
    <div
      ref={layer}
      role="presentation"
      className="absolute inset-0 z-20 cursor-crosshair"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId)
        const p = local(e)
        setDrag({ a: p, b: p })
      }}
      onPointerMove={(e) => {
        if (drag) setDrag({ ...drag, b: local(e) })
      }}
      onPointerUp={(e) => {
        if (!drag) return
        const r = layer.current?.getBoundingClientRect()
        const ox = r?.left ?? 0
        const oy = r?.top ?? 0
        const a = toBoard({ x: drag.a.x + ox, y: drag.a.y + oy })
        const b = toBoard({ x: e.clientX, y: e.clientY })
        const rect = normalise(a, b)
        setDrag(null)
        onDone(rect.w < 40 || rect.h < 40 ? { x: a.x - 240, y: a.y - 160, w: 480, h: 320 } : rect)
      }}
    >
      {box && (
        <div
          className="pointer-events-none absolute rounded-lg border-[1.5px] border-dashed border-(--focus) bg-ai-soft/40"
          style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
        />
      )}
      {!drag && (
        <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 rounded-md border border-line bg-surface px-3 py-1.5 text-[12.5px] text-ink-2 shadow-md">
          {t('toolbar.groupDrawHint')}
        </div>
      )}
    </div>
  )
}
